import React from 'react';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../ui/Button';
import { emptyInvoiceLine, invoiceLineTotal, invoiceTotals } from '../../utils/invoice';
import { formatKr } from '../../utils/quote';
import { invoiceUnits } from '../../types/invoice';
import type { InvoiceLine, InvoiceUnit } from '../../types/invoice';
interface Props {
  lines: InvoiceLine[];
  onChange: (lines: InvoiceLine[]) => void;
  disabled?: boolean;
}
const cell = 'w-full rounded-lg border border-line bg-white px-3 py-2 text-[15px] text-ink transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none disabled:cursor-not-allowed disabled:bg-canvas';
const grid = 'md:grid-cols-[1fr_4.5rem_6.5rem_6.5rem_4.5rem_4.5rem_6.5rem_4.5rem]';
export function InvoiceLinesEditor({
  lines,
  onChange,
  disabled = false
}: Props) {
  const totals = invoiceTotals(lines);
  function update(id: string, patch: Partial<InvoiceLine>) {
    onChange(lines.map(line => line.id === id ? {
      ...line,
      ...patch
    } : line));
  }
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= lines.length) return;
    const next = [...lines];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onChange(next);
  }
  return <div data-id="element-94" data-mp="3w3ty195gn4uc">
      <div className={`hidden gap-2 px-1 pb-2 text-xs uppercase tracking-wider text-ink-muted md:grid ${grid}`} data-id="element-95" data-mp="3w551f7uzuakx">
        <span data-id="element-96" data-mp="33emqjko134wy">Beskrivelse</span>
        <span data-id="element-97" data-mp="33fej0mmbwmj0">Antal</span>
        <span data-id="element-98" data-mp="33g7id2iyk9r4">Enhed</span>
        <span data-id="element-99" data-mp="3he5wc6tda463">Enhedspris</span>
        <span data-id="element-100" data-mp="3hee97yphp9ha">Rabat %</span>
        <span data-id="element-101" data-mp="3hfpek2yl2pai">Moms %</span>
        <span className="text-right" data-id="element-102" data-mp="3hbt5t59jmlhf">I alt</span>
        <span data-id="element-103" data-mp="3dbhn3y0qqv8l" />
      </div>

      <ul className="space-y-3 md:space-y-2" data-id="element-104" data-mp="3db9922svtjvt">
        {lines.map((line, index) => <li key={line.id} className={`grid gap-2 rounded-xl border border-line bg-canvas p-3 md:items-center md:border-0 md:bg-transparent md:p-0 ${grid}`} data-id="element-105" data-mp="wmm02pglp8e2" data-mpi={index}>
            <label className="md:contents" data-id="element-106" data-mp="3lg0lix85cl1x">
              <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-107" data-mp="3qfwoqu90s8gf">Beskrivelse</span>
              <input className={cell} value={line.description} disabled={disabled} placeholder="Fx montering af hegn, 12 meter" onChange={event => update(line.id, {
            description: event.target.value
          })} aria-label="Beskrivelse" data-id="element-108" data-mp="3qhpzakmr7ctp" />
            </label>

            <div className="grid grid-cols-2 gap-2 md:contents" data-id="element-109" data-mp="37q6wadsqbyro">
              <label className="md:contents" data-id="element-110" data-mp="3bqh1ho8p6l5h">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-111" data-mp="3bppfn54wk1se">Antal</span>
                <input className={cell} type="number" min={0} step="0.25" inputMode="decimal" value={line.quantity} disabled={disabled} onChange={event => update(line.id, {
              quantity: Number(event.target.value)
            })} aria-label="Antal" data-id="element-112" data-mp="3gja5ub097rh1" />
              </label>

              <label className="md:contents" data-id="element-113" data-mp="27h75a1jdug1r">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-114" data-mp="27g5qblz8x9er">Enhed</span>
                <select className={`${cell} appearance-none`} value={line.unit} disabled={disabled} onChange={event => update(line.id, {
              unit: event.target.value as InvoiceUnit
            })} aria-label="Enhed" data-id="element-115" data-mp="22ixc1wh7xpis">
                  {invoiceUnits.map(unit => <option key={unit} value={unit} data-id="element-116" data-mp="1jrmljgfenerf">
                      {unit}
                    </option>)}
                </select>
              </label>

              <label className="md:contents" data-id="element-117" data-mp="1errxrlqxf5al">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-118" data-mp="19sx41orf7wbp">Enhedspris</span>
                <input className={cell} type="number" min={0} step="1" inputMode="decimal" value={line.unitPrice} disabled={disabled} onChange={event => update(line.id, {
              unitPrice: Number(event.target.value)
            })} aria-label="Enhedspris" data-id="element-119" data-mp="39v6d9a6j7utl" />
              </label>

              <label className="md:contents" data-id="element-120" data-mp="2r3nc78w2iqu7">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-121" data-mp="2v6jzhcwjzb4h">Rabat %</span>
                <input className={cell} type="number" min={0} max={100} step="1" inputMode="numeric" value={line.discount} disabled={disabled} onChange={event => update(line.id, {
              discount: Number(event.target.value)
            })} aria-label="Rabat i procent" data-id="element-122" data-mp="2h70swoje47ua" />
              </label>

              <label className="md:contents" data-id="element-123" data-mp="24x2tlkeurk87">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-124" data-mp="1zy87o9ll9fjv">Moms %</span>
                <input className={cell} type="number" min={0} max={100} step="1" inputMode="numeric" value={line.vatRate} disabled={disabled} onChange={event => update(line.id, {
              vatRate: Number(event.target.value)
            })} aria-label="Momssats i procent" data-id="element-125" data-mp="2emclp09p52wb" />
              </label>
            </div>

            <p className="flex items-center justify-between gap-2 text-[15px] font-medium text-ink md:justify-end" data-id="element-126" data-mp="2smna7wnja5eb">
              <span className="text-xs font-normal text-ink-muted md:hidden" data-id="element-127" data-mp="136xphypbaf29">I alt inkl. moms</span>
              {formatKr(invoiceLineTotal(line))}
            </p>

            <div className="flex items-center justify-end gap-1" data-id="element-128" data-mp="yape8we8j60d">
              <button type="button" disabled={disabled || index === 0} onClick={() => move(index, -1)} aria-label="Flyt linje op" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-30" data-id="element-129" data-mp="1ny2uo93ki6j4">
                <ChevronUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-130" data-mp="26tjid5ztik75" />
              </button>
              <button type="button" disabled={disabled || index === lines.length - 1} onClick={() => move(index, 1)} aria-label="Flyt linje ned" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-30" data-id="element-131" data-mp="2auv3ay7qyh90">
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-132" data-mp="ixv5jwyccovc" />
              </button>
              <button type="button" disabled={disabled} onClick={() => onChange(lines.filter(entry => entry.id !== line.id))} aria-label="Fjern linje" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger disabled:opacity-50" data-id="element-133" data-mp="dy8thqu91eo4">
                <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-134" data-mp="wrcgitcyot9r" />
              </button>
            </div>
          </li>)}
      </ul>

      {lines.length === 0 && <p className="rounded-xl border border-dashed border-line bg-canvas px-4 py-6 text-center text-sm text-ink-muted" data-id="element-135" data-mp="mywwlqi3tum8">
          Ingen linjer endnu. Tilføj den første linje for at beregne beløbet.
        </p>}

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-136" data-mp="1fm172azyv15e">
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => onChange([...lines, emptyInvoiceLine()])} data-id="element-137" data-mp="nyzscfmb4srs">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-138" data-mp="f1frh7scwyhp" />
          Tilføj linje
        </Button>

        <dl className="min-w-[15rem] divide-y divide-line/70 text-[15px]" data-id="element-139" data-mp="jyxobzt3nn9c">
          <div className="flex justify-between gap-6 py-2" data-id="element-140" data-mp="17plbttfi0lh0">
            <dt className="text-ink-soft" data-id="element-141" data-mp="1bplqnf6ieyol">Subtotal ekskl. moms</dt>
            <dd className="font-medium text-ink" data-id="element-142" data-mp="1bnrf2rh4oix6">{formatKr(totals.subtotal)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-143" data-mp="xpudfgnthmbn">
            <dt className="text-ink-soft" data-id="element-144" data-mp="12o4ci2cnx72j">Moms</dt>
            <dd className="font-medium text-ink" data-id="element-145" data-mp="12r0a1pklltqg">{formatKr(totals.vatAmount)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-146" data-mp="3iio67irsfi87">
            <dt className="font-medium text-ink" data-id="element-147" data-mp="3ng7k2hkkpszm">Total inkl. moms</dt>
            <dd className="font-display text-lg font-bold text-ink" data-id="element-148" data-mp="3fvdpvqfk1ppw">{formatKr(totals.total)}</dd>
          </div>
        </dl>
      </div>
    </div>;
}